import React, { useCallback, useEffect, useState } from 'react'
import { Popover } from 'antd';
import Tooltip from '../../common/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../store/store'
import { reinitializeBoxWidthAndHeight } from '../../../utils/boxes'
import { isFixedImageFit } from '../../../shared/boxes/imageFit'
import { MASKS_TYPE } from '../../../constants/constants'

// Pendant image de PredefinedValuesPicker : les images prédéfinies sont portées
// par la box (cf. FormImagePredefinedValues). Une variable image ne contient
// qu'une image, donc le clic sur une vignette remplace directement la valeur —
// même traitement qu'une image de la banque (ratio recalculé au chargement).
const ImagePredefinedValuesPicker = ({ boxId, zoom }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  // Une variable peut vivre dans un masque (en-tête / pied de page) : la box
  // n'est alors pas dans `boxes`.
  const predefinedValues = useStore(({ boxes, masks }) =>
    [...boxes, ...masks.map((m) => m.boxes).flat()].find((b) => b.id === boxId)?.predefinedValues
  )
  const updateBox = useStore(({ updateBox }) => updateBox)
  const header = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.HEADER.id))
  const footer = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id))

  // L'aperçu est une liste virtualisée : en défilant, la ligne peut être
  // démontée et le popover resterait à flotter loin de son bouton. En capture,
  // le défilement d'un conteneur interne ne remontant pas jusqu'à window.
  useEffect(() => {
    if (!open) return undefined
    const close = () => setOpen(false)
    window.addEventListener('scroll', close, true)
    return () => window.removeEventListener('scroll', close, true)
  }, [open])

  const applyImage = useCallback((url) => {
    const imgEl = document.createElement('img')
    // Le ratio a besoin des dimensions réelles ; en cas d'échec de chargement on
    // pose quand même la src (hauteur auto) plutôt que de bloquer.
    const apply = () => {
      updateBox(boxId, (b, column, columnWidth) => {
        b.content = { ...b.content, src: url, ratio: imgEl.width / imgEl.height }
        // Contenir / remplir : la box garde sa taille, seule l'image change.
        if (isFixedImageFit(b)) return
        b.height = 'auto'
        // Box de masque : updateBox passe `true` en 2e argument, pas une colonne.
        if (column && typeof column === 'object' && !column.drawMode) {
          b.width = reinitializeBoxWidthAndHeight({ header, footer, box: b, columnWidth }).width
        }
      })
      setOpen(false)
    }
    imgEl.onload = apply
    imgEl.onerror = apply
    imgEl.src = url
  }, [boxId, updateBox, header, footer])

  const values = (predefinedValues ?? []).filter((value) => !!value?.url)
  if (values.length === 0) return null

  // stopPropagation : le contenu est porté par un portail mais les évènements
  // React remontent l'arbre des composants, jusqu'aux poignées de sélection de
  // la ligne du formulaire.
  const content = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8,
        width: 300,
        maxHeight: 320,
        overflowY: 'auto',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {values.map(({ url, filename }) => (
        <Tooltip key={url} title={filename} placement="top" mouseEnterDelay={0.3}>
          <button type="button" onClick={() => applyImage(url)} style={thumbButtonStyle}>
            <img src={url} alt={filename} style={thumbImgStyle} />
          </button>
        </Tooltip>
      ))}
    </div>
  )

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger='click'
      placement='rightTop'
      title={t('apply-predefined-values')}
      content={content}
      overlayStyle={{ zoom: `${zoom ?? 100}%` }}
    >
      {/* span porteur du ref : FontAwesomeIcon rend un <svg> et le Tooltip
          imbriqué réclame déjà son propre enfant. */}
      <span
        style={{ display: 'inline-flex' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip title={t('apply-predefined-values')} mouseLeaveDelay="0">
          <FontAwesomeIcon
            icon={icon({ name: 'images', style: 'solid' })}
            style={{ marginLeft: 6, color: '#5499DE', cursor: 'pointer' }}
          />
        </Tooltip>
      </span>
    </Popover>
  )
}

const thumbButtonStyle = {
  padding: 0,
  border: '1px solid rgba(11,34,57,0.12)',
  borderRadius: 6,
  background: '#fafafa',
  cursor: 'pointer',
  height: 80,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}

const thumbImgStyle = { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }

export default ImagePredefinedValuesPicker
